import { defineStore } from "pinia";
import { computed, ref } from "vue";
import { useToast } from "vue-toastification";

import {
  createDashboard as serviceCreateDashboard,
  deleteDashboard as serviceDeleteDashboard,
  updateDashboard as serviceUpdateDashboard,
} from "@follaw-fr/services/apiDashboards";
import { useUserStore } from "@follaw-fr/stores/user";
import { useRoute, useRouter } from "vue-router";
import type { DashboardZoomView } from "@commons/types/api/schema.types";
import type {
  CreateDashboardPayload,
  UpdateDashboardPayload,
} from "@commons/types/api/api.types";
import { useApplyFeaturesFilters } from "@follaw-fr/composables/useApplyFeaturesFilters";

export const useDashboardsStore = defineStore("dashboards", () => {
  const route = useRoute();
  const router = useRouter();
  const toast = useToast();
  const userStore = useUserStore();
  const { applyFeaturesFilters } = useApplyFeaturesFilters();

  // TODO: Change to storeToRef once userStore is typed ?
  const dashboards = computed<DashboardZoomView[]>(() => userStore.dashboards);
  const createdDashboard = ref<DashboardZoomView | null>(null); // Used to set the dashboard in the digest creation after dashboard creation

  const createDashboard = async (dashboard: CreateDashboardPayload) => {
    let response;
    try {
      response = await serviceCreateDashboard(dashboard);
    } finally {
      if (response) {
        createdDashboard.value = response;
        dashboards.value.push(response);
        toast.success("Veille ajoutée avec succès.");
      }
    }
  };

  const selectedDashboard = computed(() => {
    return dashboards.value.find(
      (d) => d._id === userStore.selectedDashboardId
    );
  });

  const updateDashboard = async (
    id: number,
    dashboard: UpdateDashboardPayload
  ) => {
    let response;
    try {
      response = await serviceUpdateDashboard(id, dashboard);
    } finally {
      if (response) {
        const index = dashboards.value.findIndex((d) => d._id === id);
        dashboards.value.splice(index, 1, response);
        dashboards.value[index]._id = id;
        toast.success("Veille modifiée avec succès.");
        if (response._id === userStore.selectedDashboardId) {
          applyDashboard(response._id);
        }
      }
    }
  };

  const deleteDashboard = async (id: number) => {
    let response;
    try {
      response = await serviceDeleteDashboard(id);
    } finally {
      if (response) {
        dashboards.value.splice(
          dashboards.value.findIndex((d) => d._id === id),
          1
        );
        toast.success("Veille supprimée avec succès.");
      }
    }
  };

  const applyDashboard = (id: number) => {
    const dashboard = dashboards.value.find((d) => d._id === id);

    // Set the dashboard in the topbar
    userStore.setSelectedDashboard(id);

    // set the url query for the topbar filters
    router.push({
      query: {
        ...route.query,
        dashboard: id,
        search: dashboard?.filters?.search?.length
          ? dashboard.filters.search
          : undefined,
        thematics: dashboard?.filters?.thematic_ids?.length
          ? dashboard.filters?.thematic_ids.toString()
          : undefined,
        subthematics: dashboard?.filters?.subthematic_ids?.length
          ? dashboard.filters?.subthematic_ids.toString()
          : undefined,
      },
    });

    // set the filters in the features
    applyFeaturesFilters(dashboard?.filters ?? {});
  };

  return {
    dashboards,
    selectedDashboard,
    createdDashboard,
    createDashboard,
    updateDashboard,
    deleteDashboard,
    applyDashboard,
  };
});
